import { useEffect, useState } from "react";
import { Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/base/button";
import { Checkbox } from "@/components/ui/base/checkbox";
import { Label } from "@/components/ui/base/label";
import { useStateMachine } from "@/core/arch/state_machine";
import { IdentityBackfillJobBloc } from "@/application/tools/identity_backfill_job_bloc";
import { identityBackfillJobRepository } from "@/application/tools/identity_backfill_job_repository";
import { JobStatusBadge } from "./JobStatusBadge";
import { ToolSheetShell } from "./ToolSheetShell";
import type { ToolSheetProps } from "../tools";
import type {
  IdentityBackfillCounters,
  JobStatus,
} from "@/application/tools/types";

// ── Counter labels ────────────────────────────────────────────────────────────

const COUNTER_LABELS: Record<keyof IdentityBackfillCounters, string> = {
  verifiedSucceeded: "Verified",
  duplicateDiscovered: "Duplicate found",
  markedUnrecoverable: "Marked unrecoverable",
  markedFailed: "Marked failed",
  skippedNotVerified: "Skipped (not verified)",
  alreadyTerminal: "Already terminal",
  ineligibleStage: "Ineligible stage",
  preflightScanned: "Considered",
  preflightNotVerified: "Filtered: not verified",
  preflightMissingSessionId: "Filtered: missing session",
  preflightAlreadyTerminal: "Filtered: already terminal",
  preflightMissingLifecycle: "Filtered: missing lifecycle",
  preflightErrored: "Filtered: read error",
};

const HEADER_DERIVED_KEYS: ReadonlySet<keyof IdentityBackfillCounters> =
  new Set(["preflightScanned"]);

const COUNTER_KEYS = (
  Object.keys(COUNTER_LABELS) as (keyof IdentityBackfillCounters)[]
).filter((k) => !HEADER_DERIVED_KEYS.has(k));

// ── Status badge ──────────────────────────────────────────────────────────────

// ── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({
  processed,
  total,
  considered,
  status,
}: {
  processed: number;
  total: number | null;
  considered: number;
  status: JobStatus;
}) {
  const counting = total === null;
  const pct = total && total > 0 ? Math.min(100, (processed / total) * 100) : 0;

  let leftLabel: string;
  let rightLabel: string;
  if (counting) {
    leftLabel = "Scanning candidates…";
    rightLabel = `${considered} considered`;
  } else if (total === 0) {
    leftLabel =
      status === "complete" ? "No eligible candidates" : "0 to process";
    rightLabel = `${considered} considered`;
  } else {
    leftLabel = `${processed} of ${total} eligible processed`;
    rightLabel = `${considered} considered`;
  }

  return (
    <div className="space-y-1">
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
        {counting ? (
          <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-400" />
        ) : total === 0 ? (
          <div className="bg-muted h-full w-full rounded-full" />
        ) : (
          <div
            className="h-full rounded-full bg-blue-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  );
}

// ── Elapsed time ──────────────────────────────────────────────────────────────

function formatElapsed(startedAt: string | null, completedAt: string | null) {
  if (!startedAt) return null;
  const from = Date.parse(startedAt);
  const to = completedAt ? Date.parse(completedAt) : Date.now();
  const secs = Math.round((to - from) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainder = secs % 60;
  return `${mins}m ${remainder}s`;
}

// ── Counter grid ─────────────────────────────────────────────────────────────

function parseCounters(
  raw: string | null,
): Partial<IdentityBackfillCounters> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<IdentityBackfillCounters>;
  } catch {
    return null;
  }
}

function CounterGrid({ raw }: { raw: string | null }) {
  const counters = parseCounters(raw);
  if (!counters) return null;

  const entries = COUNTER_KEYS.map((key) => ({
    key,
    // eslint-disable-next-line security/detect-object-injection -- key is a member of the COUNTER_KEYS const array
    label: COUNTER_LABELS[key],
    // eslint-disable-next-line security/detect-object-injection -- key is a member of the COUNTER_KEYS const array
    value: counters[key] ?? 0,
  })).filter(({ value }) => value > 0);

  if (entries.length === 0) return null;

  return (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
      {entries.map(({ key, label, value }) => (
        <div
          key={key}
          className="border-border bg-muted rounded-md border px-3 py-2"
        >
          <p className="text-muted-foreground text-xs">{label}</p>
          <p className="text-foreground mt-0.5 text-lg font-semibold">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

// ── Sheet ─────────────────────────────────────────────────────────────────────

export function BackfillIdentityDedupeJobSheet({ onClose }: ToolSheetProps) {
  const [state, bloc] = useStateMachine(
    () => new IdentityBackfillJobBloc(identityBackfillJobRepository),
  );
  const [allowFromUnrecoverable, setAllowFromUnrecoverable] = useState(false);

  useEffect(() => {
    bloc.resumeIfRunning();
  }, [bloc]);

  const { phase } = state;

  const job =
    phase.kind === "watching" || phase.kind === "cancelling" ? phase.job : null;

  const isRunning = job?.status === "running" || job?.status === "pending";
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const elapsed = job ? formatElapsed(job.startedAt, job.completedAt) : null;
  const isTerminal =
    job?.status === "complete" ||
    job?.status === "failed" ||
    job?.status === "cancelled";

  return (
    <ToolSheetShell
      title="Identity Dedupe Backfill"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          {phase.kind === "idle" && (
            <Button
              onClick={() => bloc.kickoff({ allowFromUnrecoverable })}
              className="w-full"
            >
              Run backfill
            </Button>
          )}
          {phase.kind === "kicking_off" && (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Starting…
            </Button>
          )}
          {phase.kind === "watching" && job?.status === "running" && (
            <Button
              variant="outline"
              onClick={() => bloc.cancel()}
              className="w-full"
            >
              Cancel
            </Button>
          )}
          {phase.kind === "cancelling" && (
            <Button disabled variant="outline" className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Cancelling…
            </Button>
          )}
          {isTerminal && (
            <Button
              variant="outline"
              onClick={() => bloc.reset()}
              className="w-full"
            >
              Run again
            </Button>
          )}
        </div>
      }
    >
      <p className="text-muted-foreground text-sm">
        Processes IDV-verified merchants without a captured identity hash.
        Captures the hash from their Stripe Identity session and detects
        duplicate-identity collisions. Runs asynchronously — progress updates
        live below.
      </p>

      {phase.kind === "idle" && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <Checkbox
            id="batch-allow-unrecoverable"
            checked={allowFromUnrecoverable}
            onCheckedChange={(checked) =>
              setAllowFromUnrecoverable(checked === true)
            }
          />
          <Label
            htmlFor="batch-allow-unrecoverable"
            className="text-xs leading-tight text-amber-900"
          >
            Re-process rows marked unrecoverable
            <span className="block font-normal text-amber-800">
              Includes rows currently in the unrecoverable stage in the
              backfill. Defaults off.
            </span>
          </Label>
        </div>
      )}

      {phase.kind === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{phase.message}</p>
        </div>
      )}

      {job && (
        <div className="border-border mt-6 space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <JobStatusBadge status={job.status} />
            {elapsed && (
              <span className="text-subtle-foreground text-xs">{elapsed}</span>
            )}
          </div>

          <ProgressBar
            processed={job.processed}
            total={job.total}
            considered={parseCounters(job.counters)?.preflightScanned ?? 0}
            status={job.status}
          />

          {job.status === "complete" && job.total === 0 && (
            <div className="border-border bg-muted text-muted-foreground rounded-md border p-3 text-sm">
              No eligible merchants to process. The breakdown below shows why
              each considered row was skipped.
            </div>
          )}

          <CounterGrid raw={job.counters} />

          {job.status === "failed" && job.error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">Error</p>
              <pre className="mt-1 overflow-x-auto text-xs text-red-600">
                {job.error}
              </pre>
            </div>
          )}

          <p className="text-subtle-foreground text-xs">Job ID: {job.id}</p>
        </div>
      )}
    </ToolSheetShell>
  );
}
